import {
  BookOpen,
  CheckCircle,
  ExternalLink,
  GraduationCap,
  Plus,
  Tags,
  Users,
  Video,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeacherDashboard } from '@/hooks/use-dashboard'
import { useTenantPath } from '@/lib/tenant'

export default function TeacherDashboardPage() {
  const tp = useTenantPath()
  const { data: dashboard, isLoading } = useTeacherDashboard()

  const stats = [
    {
      label: 'コース数',
      value: dashboard?.courseCount ?? 0,
      icon: BookOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'レッスン数',
      value: dashboard?.lessonCount ?? 0,
      icon: Video,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: '受講者数',
      value: dashboard?.studentCount ?? 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: '完了率',
      value: `${dashboard?.completionRate ?? 0}%`,
      icon: CheckCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ]

  const menuItems = [
    {
      to: tp('/teacher/courses'),
      icon: BookOpen,
      label: 'コース管理',
      description: 'コースの作成・編集',
    },
    {
      to: tp('/teacher/lessons'),
      icon: Video,
      label: 'レッスン管理',
      description: 'レッスンの作成・編集',
    },
    { to: tp('/teacher/tags'), icon: Tags, label: 'タグ管理', description: 'カテゴリ・タグの管理' },
    {
      to: tp('/teacher/students'),
      icon: Users,
      label: '受講者一覧',
      description: '受講者の進捗確認',
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">講師ダッシュボード</h1>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </CardTitle>
              <div className={`rounded-full p-2 ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stat.value}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">クイックアクション</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to={tp('/teacher/courses/new')}>
              <Plus className="mr-2 h-4 w-4" />
              新規コース作成
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={tp('/teacher/lessons/new')}>
              <Plus className="mr-2 h-4 w-4" />
              新規レッスン作成
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Recent Courses */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">最近のコース</CardTitle>
          <CardDescription>最新5件のコース</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : dashboard?.recentCourses && dashboard.recentCourses.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recentCourses.map((course) => (
                <Link
                  key={course.id}
                  to={tp(`/teacher/courses/${course.id}/edit`)}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{course.title}</span>
                  </div>
                  <Badge variant="outline">
                    {new Date(course.createdAt).toLocaleDateString('ja-JP')}
                  </Badge>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">コースがまだありません</p>
          )}
        </CardContent>
      </Card>

      {/* Navigation Menu */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {menuItems.map((item) => (
          <Link key={item.to} to={item.to}>
            <Card className="hover:shadow-md transition-shadow h-full">
              <CardHeader className="flex flex-row items-center space-x-2 space-y-0 pb-2">
                <item.icon className="h-5 w-5 text-emerald-600" />
                <CardTitle className="text-base">{item.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Manual Link */}
      <Card>
        <CardContent className="flex items-center gap-2 pt-6">
          <GraduationCap className="h-5 w-5 text-muted-foreground" />
          <Link
            to={tp('/teacher/manual')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            講師向けマニュアル
            <ExternalLink className="h-3 w-3" />
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
